import { useEffect, useState } from 'react';
import { useIntegrationApp } from '@membranehq/react';
import { SelectBox, Option } from 'src/components/SelectBox';
import { Loading } from 'src/components/Loading';
import { ActionKey } from 'src/constants';
import { IntegrationKey } from 'src/services/integration-app.service';
import { useNotification } from 'src/contexts/NotificationContext';
import { useCrmSync } from '../../contexts/CrmSyncContext';
import { useCampaignContext } from '../../contexts/CampaignContext';
import { useExtraStepConfiguration } from '../FieldMapping/ExtraStepConfiguration';

interface MondayBoard {
  id: string;
  name: string;
  fields?: Record<string, string>;
}

interface MondayGroup {
  id: string;
  name: string;
}

export const MondayBoardSelector = () => {
  const [boards, setBoards] = useState<Option[]>([]);
  const [boardsData, setBoardsData] = useState<MondayBoard[]>([]);
  const [groups, setGroups] = useState<Option[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const integrationApp = useIntegrationApp();
  const notification = useNotification();

  const { mondayConfig, setMondayConfig, syncInProgress } = useCrmSync();
  const { isInitializing } = useCampaignContext();
  const { setIsConfigurationComplete } = useExtraStepConfiguration();
  const disabled = syncInProgress || isInitializing;

  useEffect(() => {
    const loadBoards = async () => {
      setLoadingBoards(true);
      try {
        const result = await integrationApp
          .connection(IntegrationKey.MONDAY)
          .action(ActionKey.LIST_BOARDS)
          .run({});

        const boardsWithFields: MondayBoard[] = result.output.records || [];
        setBoardsData(boardsWithFields);
      } catch (error) {
        console.error('Failed to load Monday.com boards:', error);
        setBoardsData([]);

        // Check if it's a baseUri configuration error
        if (error instanceof Error && error.message.includes('baseUri')) {
          notification.error(
            'Monday.com integration not properly configured. Please check Integration App settings.',
          );
        } else {
          notification.error('Failed to load Monday.com boards.');
        }
      } finally {
        setLoadingBoards(false);
      }
    };

    loadBoards();
  }, [integrationApp, notification]);

  // Update boards whenever boardsData changes
  useEffect(() => {
    const boardOptions: Option[] = boardsData.map((board: MondayBoard) => ({
      value: board.id,
      label: board.name,
    }));

    setBoards(boardOptions);
  }, [boardsData]);

  useEffect(() => {
    const loadGroupsAndFields = async () => {
      if (!mondayConfig.boardId) {
        setGroups([]);
        setMondayConfig((prev) => ({
          ...prev,
          boardFields: [],
        }));
        return;
      }

      setLoadingGroups(true);
      try {
        // Load groups
        const groupsResult = await integrationApp
          .connection(IntegrationKey.MONDAY)
          .action(ActionKey.LIST_GROUPS)
          .run({ board_id: mondayConfig.boardId });

        const groupOptions: Option[] =
          groupsResult.output.records?.map((group: MondayGroup) => ({
            value: group.id,
            label: group.name,
          })) || [];

        setGroups(groupOptions);
      } catch (error) {
        console.error('Failed to load Monday.com groups:', error);
        setGroups([]);

        // Check if it's a baseUri configuration error
        if (error instanceof Error && error.message.includes('baseUri')) {
          notification.error(
            'Monday.com integration not properly configured. Please check Integration App settings.',
          );
        } else {
          notification.error('Failed to load Monday.com groups.');
        }
      } finally {
        setLoadingGroups(false);
      }
    };

    loadGroupsAndFields();
  }, [mondayConfig.boardId, integrationApp, notification, setMondayConfig]);

  // Separate useEffect for handling field extraction
  useEffect(() => {
    if (!mondayConfig.boardId || !boardsData.length) {
      return;
    }

    // Find the selected board and extract its fields
    const selectedBoardData = boardsData.find(
      (board) => board.id === mondayConfig.boardId,
    );
    if (selectedBoardData && selectedBoardData.fields) {
      const fields: Option[] = Object.keys(selectedBoardData.fields).map(
        (fieldName) => ({
          value: selectedBoardData.fields![fieldName],
          label: fieldName,
        }),
      );
      setMondayConfig((prev) => ({
        ...prev,
        boardFields: fields,
      }));
    }
  }, [mondayConfig.boardId, boardsData, setMondayConfig]);

  useEffect(() => {
    const isComplete = !!(mondayConfig.boardId && mondayConfig.groupId);
    setIsConfigurationComplete(isComplete);
  }, [mondayConfig.boardId, mondayConfig.groupId, setIsConfigurationComplete]);

  const handleBoardChange = (value: string) => {
    setMondayConfig((prev) => ({
      ...prev,
      boardId: value,
      groupId: null,
      boardFields: [],
    }));
  };

  const handleGroupChange = (value: string) => {
    setMondayConfig((prev) => ({
      ...prev,
      groupId: value || null,
    }));
  };

  return (
    <div className="m-6 bg-neutral-0 border border-neutral-200 rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">Select Monday.com Board</h3>
        <div className="text-sm text-neutral-600 mb-4">
          Choose the board from which you want to filter contacts.
        </div>

        {loadingBoards ? (
          <div className="flex items-center gap-2 text-neutral-600">
            <Loading size="6" />
            <div className="text-sm">Loading boards...</div>
          </div>
        ) : (
          <>
            <SelectBox
              options={boards}
              value={
                boards.find((board) => board.value === mondayConfig.boardId) ||
                null
              }
              onChange={handleBoardChange}
              variant="outlined"
              size="medium"
              disabled={disabled || boards.length === 0}
            />

            {boards.length === 0 && (
              <div className="text-sm text-error mt-2">
                No boards found. Please check your Monday.com connection.
              </div>
            )}
          </>
        )}
      </div>

      {mondayConfig.boardId && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Select Group</h3>
          <div className="text-sm text-neutral-600 mb-4">
            Choose a specific group within the board to filter contacts further.
          </div>

          {loadingGroups ? (
            <div className="flex items-center gap-2 text-neutral-600">
              <Loading size="6" />
              <div className="text-sm">Loading groups...</div>
            </div>
          ) : (
            <>
              <SelectBox
                options={groups}
                value={
                  groups.find(
                    (group) => group.value === mondayConfig.groupId,
                  ) || null
                }
                onChange={handleGroupChange}
                variant="outlined"
                size="medium"
                disabled={disabled || groups.length === 0}
              />

              {groups.length === 0 && mondayConfig.boardId && (
                <div className="text-sm text-error mt-2">
                  No groups found for this board.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
